/**
 * Extension Bridge Utility
 * Communicates with the YouTube Transcript Helper Chrome Extension
 * to fetch video transcripts from the client-side
 */

/* global chrome */

/**
 * Check if the extension is installed and available
 * Uses multiple detection methods for reliability
 * @returns {Promise<boolean>}
 */
export async function isExtensionAvailable() {
  console.log('[ExtensionBridge] Checking extension availability...');
  console.log('[ExtensionBridge] Global marker:', window.__youtubeTranscriptHelperAvailable);
  console.log('[ExtensionBridge] Chrome runtime:', typeof chrome !== 'undefined' ? 'available' : 'not available');
  
  return new Promise((resolve) => {
    // Method 1: Try to detect extension via injected marker
    // The content script should set a global variable
    if (window.__youtubeTranscriptHelperAvailable) {
      console.log('[ExtensionBridge] Extension detected via global marker');
      resolve(true);
      return;
    }

    // Method 2: Try direct chrome.runtime communication
    // This works if we can find the extension ID
    if (typeof chrome !== 'undefined' && chrome.runtime) {
      console.log('[ExtensionBridge] Trying chrome.runtime.sendMessage...');
      // Try to send a message - if extension exists, it will respond or we'll get lastError
      const testRequestId = `ping-${Date.now()}`;
      chrome.runtime.sendMessage(
        { action: 'ping', requestId: testRequestId },
        (response) => {
          if (chrome.runtime.lastError) {
            console.log('[ExtensionBridge] chrome.runtime error:', chrome.runtime.lastError.message);
            // Extension not found, try content script method
            checkViaContentScript(resolve);
          } else {
            console.log('[ExtensionBridge] Extension responded via chrome.runtime');
            resolve(true);
          }
        }
      );
    } else {
      console.log('[ExtensionBridge] chrome.runtime not available, trying content script method');
      // Method 3: Content script message passing
      checkViaContentScript(resolve);
    }
  });
}

/**
 * Check extension availability via content script message passing
 */
function checkViaContentScript(resolve) {
  console.log('[ExtensionBridge] Trying content script message passing...');
  const testRequestId = `test-${Date.now()}`;
  let resolved = false;
  
  const timeout = setTimeout(() => {
    if (!resolved) {
      resolved = true;
      window.removeEventListener('message', testHandler);
      console.log('[ExtensionBridge] Content script check timed out - extension not detected');
      resolve(false);
    }
  }, 2000); // Increased timeout

  const testHandler = (event) => {
    console.log('[ExtensionBridge] Received message:', event.data);
    // Accept messages from same origin
    if (event.origin !== window.location.origin) {
      console.log('[ExtensionBridge] Message from different origin, ignoring');
      return;
    }

    if (
      event.data &&
      event.data.type === 'YOUTUBE_TRANSCRIPT_RESPONSE' &&
      event.data.requestId === testRequestId
    ) {
      if (!resolved) {
        resolved = true;
        clearTimeout(timeout);
        window.removeEventListener('message', testHandler);
        console.log('[ExtensionBridge] Extension detected via content script');
        resolve(true);
      }
    }
  };

  window.addEventListener('message', testHandler);
  console.log('[ExtensionBridge] Sending test message with requestId:', testRequestId);

  // Send test request
  window.postMessage(
    {
      type: 'YOUTUBE_TRANSCRIPT_REQUEST',
      videoId: 'test',
      requestId: testRequestId,
    },
    window.location.origin
  );
}

/**
 * Fetch transcript for a YouTube video using the extension
 * @param {string} videoId - YouTube video ID
 * @returns {Promise<{success: boolean, transcript: string | null, error: string | null, language: string | null}>}
 */
export async function fetchTranscript(videoId) {
  return new Promise((resolve, reject) => {
    const requestId = `req-${Date.now()}-${Math.random()}`;
    const timeout = setTimeout(() => {
      window.removeEventListener('message', handler);
      reject(new Error('Extension timeout: No response from extension'));
    }, 30000); // 30 second timeout

    const handler = (event) => {
      // Security: only accept messages from same origin
      if (event.origin !== window.location.origin) {
        return;
      }

      if (
        event.data &&
        event.data.type === 'YOUTUBE_TRANSCRIPT_RESPONSE' &&
        event.data.requestId === requestId
      ) {
        clearTimeout(timeout);
        window.removeEventListener('message', handler);

        console.log('[ExtensionBridge] Transcript response:', {
          success: event.data.success,
          hasTranscript: !!event.data.transcript,
          transcriptLength: event.data.transcript?.length,
          error: event.data.error,
          fullResponse: event.data
        });
        
        if (event.data.success && event.data.transcript) {
          resolve({
            success: true,
            transcript: event.data.transcript,
            error: event.data.error || null,
            language: event.data.language || null,
          });
        } else {
          resolve({
            success: event.data.success || false,
            transcript: null,
            error: event.data.error || 'No transcript available',
            language: null,
          });
        }
      }
    };

    window.addEventListener('message', handler);

    // Send request to extension via content script
    window.postMessage(
      {
        type: 'YOUTUBE_TRANSCRIPT_REQUEST',
        videoId: videoId,
        requestId: requestId,
      },
      window.location.origin
    );
  });
}

/**
 * Get installation instructions for the extension
 * @returns {string}
 */
export function getInstallationInstructions() {
  return `
To enable client-side transcription, please install the YouTube Transcript Helper extension:

1. Open Chrome and navigate to chrome://extensions/
2. Enable "Developer mode" (toggle in top-right)
3. Click "Load unpacked"
4. Select the youtube-helper-extension directory from the project root
5. Refresh this page

The extension allows the dashboard to fetch YouTube transcripts using your browser's IP address, bypassing server-side blocking.
  `.trim();
}
